import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

const dietarySchema = z.object({
  name: z.string().min(1, "Please enter a tag name"),
});

type DietarySchema = z.infer<typeof dietarySchema>;

export interface DietaryFormValues {
  id?: string;
  name: string;
}

interface DietaryFormProps {
  initialValues?: Partial<DietaryFormValues>;
  onSave: (values: DietaryFormValues) => void;
  onReset?: () => void;
  isSubmitting?: boolean;
}

const DietaryForm = ({ initialValues, onSave, onReset, isSubmitting = false }: DietaryFormProps) => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<DietarySchema>({
    resolver: zodResolver(dietarySchema),
    defaultValues: { name: initialValues?.name ?? "" },
  });

  const onSubmit = (data: DietarySchema) => onSave({ id: initialValues?.id, ...data });

  const handleReset = () => {
    reset({ name: initialValues?.name ?? "" });
    onReset?.();
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      noValidate
      className="bg-white rounded-[8px] px-[33px] pt-[33px] pb-[49px] flex flex-col gap-6"
      style={{
        border: "1px solid rgba(194,199,201,0.1)",
        boxShadow: "0px 20px 25px -5px rgba(0,0,0,0.1), 0px 8px 10px -6px rgba(0,0,0,0.1)",
      }}
    >
      <div className="flex flex-col gap-2">
        <label className="text-[12px] font-medium text-content-label">
          Tag Name
        </label>
        <input
          type="text"
          {...register("name")}
          placeholder="e.g. Vegan"
          className="w-full rounded-[6px] px-4 py-[13px] text-[14px] font-normal outline-none placeholder:text-[#94a3b8] bg-surface-input text-content-body"
        />
        {errors.name && (
          <span className="text-[11px] text-content-error">
            {errors.name.message}
          </span>
        )}
      </div>

      <div className="flex flex-col gap-3 pt-4">
        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full h-[45px] rounded-[20px] text-white text-[16px] font-bold tracking-[0.64px] transition-opacity hover:opacity-90 disabled:opacity-60 bg-brand-accent"
        >
          {isSubmitting ? (
            <span className="flex items-center justify-center gap-2">
              <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
              </svg>
              Saving…
            </span>
          ) : "Save"}
        </button>
        <button
          type="button"
          onClick={handleReset}
          className="w-full py-3 rounded-[6px] text-[14px] font-semibold text-center transition-opacity hover:opacity-70 text-brand-teal"
        >
          Reset Changes
        </button>
      </div>
    </form>
  );
};

export default DietaryForm;
